import * as Sentry from '@sentry/react';
import { tanstackRouterBrowserTracingIntegration } from '@sentry/react'; // §5 — verified PRESENT at install (ADR 0015)
import posthog from 'posthog-js'; // SAME npm singleton as lib/posthog.ts — module-level dedup is the contract
import type { router } from '../App'; // type-only import of the router instance type (no runtime cycle)

// PROD-gate read ONCE at module scope so Vite DCEs the init in dev (mirror debug-realtime.ts:16-21 — §4.E, §11 #7).
// import.meta.env.PROD is Vite's static boolean (true in `vite build`, false in `vite dev`) — replaced at build time.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined; // GH var (D3); undefined in dev
const RELEASE = import.meta.env.VITE_RELEASE_SHA as string | undefined; // from 4.1 define

/**
 * Codes that represent expected client-error responses — drop from Sentry,
 * route to logger only (cf. Phase 2 § 2.6 api-side `EXPECTED_4XX_CODES`).
 *
 * **DRIFT WARNING**: this set MUST stay byte-identical to the api-side
 * `EXPECTED_4XX_CODES` in `apps/api/src/instrument.ts`. Web does NOT import
 * `ErrorCodes` from api; literal strings duplicated here. The drift-lock
 * test at `lib/__tests__/sentry.test.ts` asserts membership equality
 * against the api-side source-of-truth.
 *
 * Source (Phase 2 § 2.6 final state, verified at codebase_sha 237a3f8):
 *   apps/api/src/instrument.ts:6-15
 *   apps/api/src/core/errors/error-codes.ts:6-19
 */
export const EXPECTED_4XX_CODES = new Set<string>([
  'PAYMENT_REQUIRED',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'RATE_LIMITED',
  'RESYNC_RATE_LIMIT',
  'PUBLIC_SHARE_RATE_LIMIT',
]);

/**
 * Sentry beforeSend filter — extracted at module scope for testability.
 * Drops browser-extension frames AND expected 4xx error codes.
 *
 * ADDITIVE — no behavior change to production; `initSentry` passes this
 * directly to `Sentry.init({ beforeSend })`.
 *
 * Phase 3 § 3.5 extension: code extraction fallback chain mirrors api-side
 * pattern (`event.tags?.code` primary, `event.extra?.code` secondary,
 * `event.contexts?.error_data?.code` tertiary).
 */
export function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Drop browser-extension frames (spec §4 "beforeSend drops extension frames").
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  if (
    frames.some(
      (f) =>
        f.filename?.startsWith('chrome-extension://') ||
        f.filename?.startsWith('moz-extension://')
    )
  ) {
    return null;
  }
  // NEW Phase 3 § 3.5 — drop expected 4xx codes (rate-limit, payment-required, etc.).
  // Capture sites that propagate ApiError set `tags: { code: err.code }` so the
  // discriminator path is `event.tags?.code`. Fallback chain matches api-side
  // pattern (event.extra?.code, event.contexts?.error_data?.code).
  const code =
    (event.tags?.code as string | undefined) ??
    (event.extra?.code as string | undefined) ??
    (event.contexts?.error_data as { code?: string } | undefined)?.code;
  if (code && EXPECTED_4XX_CODES.has(code)) {
    return null;
  }
  return event;
}

export function initSentry(routerInstance: typeof router): void {
  if (!import.meta.env.PROD || !SENTRY_DSN) return; // never init in dev; never init without a DSN (§11 #7, ADR 0004)
  Sentry.init({
    dsn: SENTRY_DSN,
    release: RELEASE, // MUST equal the sentryVitePlugin release from 4.1 (§10 symmetry, §11 #8)
    integrations: [
      // Router-aware variant REPLACES plain browserTracingIntegration() (§6, ADR 0015).
      tanstackRouterBrowserTracingIntegration(routerInstance),
      Sentry.replayIntegration({
        maskAllText: false, // spec value wins over the ecosystem sample's true (§6; spec §4)
        maskAllInputs: true, // NON-NEGOTIABLE — inputs hold sensitive rents/costs (§11 #4; AC-gated)
        blockAllMedia: true,
      }),
      // observability-stack FEATURE_END (2026-05-24) — Item 12, cross-tool traversal AC.
      // PostHog session-replay link enrichment: attaches the PostHog session-replay URL
      // as a tag on every Sentry event so the Sentry UI surfaces a clickable "View Replay"
      // link. Code-level integration (not Sentry marketplace) — works on free Sentry tier.
      // Init-order safe: this just registers an integration object holding a posthog ref;
      // the integration's hook runs at Sentry-event-capture time, by which point
      // initPostHog() has already run in main.tsx and posthog is fully initialized.
      posthog.sentryIntegration({
        organization: 'arcanum-hq', // Sentry org slug
        projectId: 4511442417680384, // arcanum-web Sentry numeric project ID (from DSN tail)
      }),
    ],
    replaysOnErrorSampleRate: 1.0, // record on error (§11 #9)
    replaysSessionSampleRate: 0.0, // never record all sessions (§11 #9)
    beforeSend,
  });
}
